import React from "react";

import Modal from "@cloudscape-design/components/modal";
import Input from "@cloudscape-design/components/input";
import Button from "@cloudscape-design/components/button";
import Box from "@cloudscape-design/components/box";
import SpaceBetween from "@cloudscape-design/components/space-between";
import Spinner from "@cloudscape-design/components/spinner";
import Multiselect from "@cloudscape-design/components/multiselect";
import FormField from "@cloudscape-design/components/form-field";
import Header from "@cloudscape-design/components/header";
import Alert from "@cloudscape-design/components/alert";
import Table from "@cloudscape-design/components/table";
import ColumnLayout from "@cloudscape-design/components/column-layout";
import Container from "@cloudscape-design/components/container";
import Select from "@cloudscape-design/components/select";

/* Recoil */
import { useRecoilValue, useSetRecoilState } from "recoil";
import { configStore } from "../stores/config";
import { staffAddModalStore } from "../stores/staffAddModal";
import { userStore } from "../stores/access";
import { staffStore } from "../stores/staff";
/* ------- */

const DEFAULT_STATE = {
	show: false,
	stage: "form",
	form: {
		login: "",
		password: "",
		level: "",
		brandIds: [],
		defaultGroupIds: [],
		tfa: true,
	},
	groups: [],
	groupsLoaded: false,
	groupsError: null,
	visibleProjects: [],
	visibleProjectsLoaded: false,
	visibleProjectsError: "",
	visibleProjectsLogin: "",
	result: {
		secret: null,
		brands: [],
	},
	error: { text: "", code: 0, timestamp: 0 },
};

class StaffAddUserModal extends React.Component {
	constructor(props) {
		super(props);
		this.state = {
			showPassword: false,
		};
		this.userContextVersion = 0;
	}

	componentDidMount() {
		if (!this.props.staffAddModal.groupsLoaded) {
			this.loadGroups();
		}
		if (this.props.staffAddModal.show) {
			this.loadVisibleProjects();
		}
	}

	componentDidUpdate(prevProps) {
		const wasOpen = prevProps.staffAddModal && prevProps.staffAddModal.show;
		const isOpen = this.props.staffAddModal && this.props.staffAddModal.show;
		const prevLogin = prevProps.user && prevProps.user.login;
		const currentLogin = this.props.user && this.props.user.login;
		if (prevLogin !== currentLogin) {
			this.resetForCurrentUserChange(isOpen);
			if (isOpen) {
				this.loadGroups();
				this.loadVisibleProjects();
			}
			return;
		}
		if (!wasOpen && isOpen) {
			if (!this.props.staffAddModal.groupsLoaded) {
				this.loadGroups();
			}
			this.loadVisibleProjects();
		}
	}

	getDefaultState(show) {
		return {
			...DEFAULT_STATE,
			show: !!show,
			form: { ...DEFAULT_STATE.form },
			groups: [],
			visibleProjects: [],
			result: { ...DEFAULT_STATE.result },
			error: { ...DEFAULT_STATE.error },
		};
	}

	resetForCurrentUserChange(show) {
		this.userContextVersion += 1;
		this.setState({ showPassword: false });
		this.props.setStaffAddModal(this.getDefaultState(show));
	}

	loadGroups() {
		const loadUserContextVersion = this.userContextVersion;
		this.props.setStaffAddModal((s0) => ({
			...s0,
			groupsLoaded: false,
			groupsError: null,
		}));
		this.props.Interface
			.request("get", { class: "settings", module: "staff", method: "groups" }, {})
			.then((list) => {
				if (this.userContextVersion !== loadUserContextVersion) return;
				this.props.setStaffAddModal((s) => ({
					...s,
					groups: Array.isArray(list) ? list : [],
					groupsLoaded: true,
					groupsError: null,
				}));
			})
			.catch((e) => {
				if (this.userContextVersion !== loadUserContextVersion) return;
				const text = "Failed to load groups: " + ((e && e.text) || "unknown");
				this.props.setStaffAddModal((s) => ({
					...s,
					groups: [],
					groupsLoaded: true,
					groupsError: text,
				}));
				this.props.Interface.addNoty("Error", text);
			});
	}

	loadVisibleProjects() {
		const login = this.props.user && this.props.user.login;
		if (!login) {
			const text = "Failed to load visible projects: missing current user login";
			this.props.setStaffAddModal((s) => ({
				...s,
				visibleProjects: [],
				visibleProjectsLoaded: true,
				visibleProjectsError: text,
				visibleProjectsLogin: "",
				form: this.pruneFormBrands(s.form, []),
				error: { text: "", code: 0, timestamp: 0 },
			}));
			this.props.Interface.addNoty("Error", text);
			return;
		}

		const startedLogin = login;
		const loadUserContextVersion = this.userContextVersion;
		this.props.setStaffAddModal((s0) => ({
			...s0,
			visibleProjects: [],
			visibleProjectsLoaded: false,
			visibleProjectsError: "",
			visibleProjectsLogin: startedLogin,
			error: { text: "", code: 0, timestamp: 0 },
		}));

		this.props.Interface
			.request("get", { class: "settings", module: "staff", method: "person" }, { login: startedLogin })
			.then((person) => {
				const currentModal = this.props.staffAddModal || {};
				const currentLogin = this.props.user && this.props.user.login;
				if (!currentModal.show || currentLogin !== startedLogin || this.userContextVersion !== loadUserContextVersion) return;

				const visibleProjects = this.normalizeVisibleProjects((person && person.projects) || []);
				this.props.setStaffAddModal((s) => ({
					...s,
					visibleProjects,
					visibleProjectsLoaded: true,
					visibleProjectsError: "",
					visibleProjectsLogin: startedLogin,
					form: this.pruneFormBrands(s.form, visibleProjects),
					error: { text: "", code: 0, timestamp: 0 },
				}));
			})
			.catch((e) => {
				const currentModal = this.props.staffAddModal || {};
				const currentLogin = this.props.user && this.props.user.login;
				if (!currentModal.show || currentLogin !== startedLogin || this.userContextVersion !== loadUserContextVersion) return;

				const text = "Failed to load visible projects: " + ((e && e.text) || "unknown");
				this.props.setStaffAddModal((s) => ({
					...s,
					visibleProjects: [],
					visibleProjectsLoaded: true,
					visibleProjectsError: text,
					visibleProjectsLogin: startedLogin,
					form: this.pruneFormBrands(s.form, []),
					error: { text: "", code: 0, timestamp: 0 },
				}));
				this.props.Interface.addNoty("Error", text);
			});
	}

	normalizeVisibleProjects(projects) {
		const seen = {};
		const normalized = [];
		(Array.isArray(projects) ? projects : []).forEach((project) => {
			if (!project) return;
			const brand = project.brand;
			if (brand == null) return;
			const key = String(brand);
			if (seen[key]) return;
			seen[key] = true;
			normalized.push({
				...project,
				brand,
				brandName: project.brandName || this.publicBrandName(brand) || ("#" + brand),
			});
		});
		return normalized;
	}

	pruneFormBrands(form, visibleProjects) {
		const visibleIds = {};
		(Array.isArray(visibleProjects) ? visibleProjects : []).forEach((project) => {
			if (project && project.brand != null) {
				visibleIds[String(project.brand)] = true;
			}
		});

		const prevBrandIds = Array.isArray(form.brandIds) ? form.brandIds : [];
		const nextBrandIds = prevBrandIds.filter((brandId) => visibleIds[String(brandId)]);

		const brandIdsChanged =
			nextBrandIds.length !== prevBrandIds.length ||
			nextBrandIds.some((brandId, index) => brandId !== prevBrandIds[index]);

		if (!brandIdsChanged) return form;
		return { ...form, brandIds: nextBrandIds };
	}

	brandsList() {
		return (this.props.user && this.props.user.brandsList) || [];
	}

	visibleProjects() {
		return (this.props.staffAddModal && this.props.staffAddModal.visibleProjects) || [];
	}

	publicBrandName(brandId) {
		const hit = this.brandsList().find((b) => b && String(b.id) === String(brandId));
		return hit && hit.name ? hit.name : null;
	}

	brandName(brandId) {
		const visibleHit = this.visibleProjects().find((p) => p && String(p.brand) === String(brandId));
		return (visibleHit && visibleHit.brandName) || this.publicBrandName(brandId) || ("#" + brandId);
	}

	brandOptions() {
		return this.visibleProjects().map((project) => ({
			value: String(project.brand),
			label: project.brandName || this.publicBrandName(project.brand) || String(project.brand),
		}));
	}

	groupOptions() {
		const groups = this.props.staffAddModal.groups || [];
		return groups.map((g) => ({
			value: String(g.id),
			label: g.name,
			description: g.description || undefined,
		}));
	}

	updateForm(patch) {
		const s = this.props.staffAddModal;
		this.props.setStaffAddModal({ ...s, form: { ...s.form, ...patch } });
	}

	setBrandIds(brandIds) {
		const s = this.props.staffAddModal;
		const visibleIds = {};
		(s.visibleProjects || []).forEach((project) => {
			if (project && project.brand != null) visibleIds[String(project.brand)] = true;
		});
		const filteredBrandIds = (brandIds || []).filter((brandId) => visibleIds[String(brandId)]);
		this.props.setStaffAddModal({
			...s,
			form: { ...s.form, brandIds: filteredBrandIds },
		});
	}

	validateForm(formCandidate) {
		const state = this.props.staffAddModal;
		const form = formCandidate || state.form;
		const callerLevel = Number(this.props.user.level) || 0;
		const visibleProjectIds = {};
		(state.visibleProjects || []).forEach((project) => {
			if (project && project.brand != null) visibleProjectIds[String(project.brand)] = true;
		});

		const login = (form.login || "").trim();
		if (login.length < 4 || login.length > 64) {
			return "Login must be 4..64 characters";
		}
		if (typeof form.password !== "string" || form.password.length < 8 || form.password.length > 128) {
			return "Password must be 8..128 characters";
		}
		const lvl = Number(form.level);
		if (!Number.isFinite(lvl) || lvl < 1 || lvl > callerLevel - 1) {
			return "Level must be 1..." + (callerLevel - 1);
		}
		if (state.visibleProjectsError) {
			return state.visibleProjectsError;
		}
		if (state.groupsError) {
			return state.groupsError;
		}
		if (!state.groupsLoaded) {
			return "Groups are still loading";
		}
		if (!state.visibleProjectsLoaded) {
			return "Visible projects are still loading";
		}
		if (!Object.keys(visibleProjectIds).length) {
			return "No visible projects are available for your account";
		}
		if (!Array.isArray(form.brandIds) || form.brandIds.length === 0) {
			return "Select at least one project";
		}
		if (form.brandIds.some((brandId) => !visibleProjectIds[String(brandId)])) {
			return "Selected projects are no longer visible. Please choose visible projects only.";
		}
		if (typeof form.tfa !== "boolean") {
			return "Invalid `tfa` flag";
		}
		return null;
	}

	onCreate() {
		const stateBeforePrune = this.props.staffAddModal;
		const prunedForm = this.pruneFormBrands(stateBeforePrune.form, stateBeforePrune.visibleProjects || []);
		const stateForCreate = prunedForm === stateBeforePrune.form
			? stateBeforePrune
			: { ...stateBeforePrune, form: prunedForm };
		if (prunedForm !== stateBeforePrune.form) {
			this.props.setStaffAddModal(stateForCreate);
		}

		const err = this.validateForm(prunedForm);
		if (err) {
			this.props.setStaffAddModal({
				...stateForCreate,
				form: prunedForm,
				error: { text: err, code: 0, timestamp: Date.now() },
			});
			this.props.Interface.addNoty("Error", err);
			return;
		}
		const s0 = stateForCreate;
		const form = prunedForm;
		const login = (form.login || "").trim();
		const level = Number(form.level);
		const createUserContextVersion = this.userContextVersion;
		const createUserLogin = this.props.user && this.props.user.login;

		const brandsPayload = form.brandIds.map((brandId) => ({
			brandId: brandId,
			groups: (form.defaultGroupIds || []).map((v) => ({ value: Number(v) })),
		}));

		this.props.setStaffAddModal({
			...s0,
			stage: "pending",
			form: prunedForm,
			error: { text: "", code: 0, timestamp: 0 },
			result: { secret: null, brands: [] },
		});

		this.props.Interface
			.request(
				"set",
				{ class: "settings", module: "staff", method: "create" },
				{
					name: login,
					password: form.password,
					level: level,
					tfa: true,
					brands: brandsPayload,
				},
			)
			.then((resp) => {
				if (this.userContextVersion !== createUserContextVersion || ((this.props.user && this.props.user.login) !== createUserLogin)) return;
				const validResponse =
					resp &&
					typeof resp === "object" &&
					!Array.isArray(resp) &&
					Array.isArray(resp.brands) &&
					(typeof resp.secret === "string" || resp.secret === null);
				if (!validResponse) {
					const errorText = "Invalid create response from server";
					const s = this.props.staffAddModal;
					this.props.setStaffAddModal({
						...s,
						stage: "form",
						error: { text: errorText, code: 0, timestamp: Date.now() },
					});
					this.props.Interface.addNoty("Error", errorText);
					return;
				}
				const secret = resp.secret;
				const rawBrands = resp.brands;
				const brands = rawBrands.map((r) => ({
					brandId: r && r.brandId,
					userId: r && r.userId,
					brandName: this.brandName(r && r.brandId),
				}));
				const s = this.props.staffAddModal;
				this.props.setStaffAddModal({
					...s,
					stage: "success",
					result: { secret: secret, brands: brands },
				});
				this.triggerStaffReload();
				this.props.Interface.addNoty("Success", "User `" + login + "` created");
			})
			.catch((e) => {
				if (this.userContextVersion !== createUserContextVersion || ((this.props.user && this.props.user.login) !== createUserLogin)) return;
				const errorText = e && typeof e.text === "string" ? e.text : "Unable to create staff user";
				const s = this.props.staffAddModal;
				this.props.setStaffAddModal({
					...s,
					stage: "form",
					error: {
						text: errorText,
						code: (e && e.code) || 0,
						timestamp: (e && e.timestamp) || Date.now(),
					},
				});
				this.props.Interface.addNoty("Error", errorText);
			});
	}

	triggerStaffReload() {
		this.props.setStaff((staff) => ({
			...staff,
			loaded: false,
			reloadSignal: (staff.reloadSignal || 0) + 1,
		}));
	}

	onDone() {
		this.props.setStaffAddModal({ ...DEFAULT_STATE });
	}

	onClose() {
		this.props.setStaffAddModal({ ...DEFAULT_STATE });
	}

	onCopy(text, label) {
		if (navigator && navigator.clipboard && navigator.clipboard.writeText) {
			navigator.clipboard.writeText(text).then(
				() => this.props.Interface.addNoty("Success", label + " copied"),
				() => this.props.Interface.addNoty("Error", "Clipboard unavailable"),
			);
		} else {
			this.props.Interface.addNoty("Error", "Clipboard unavailable");
		}
	}

	selectAllOption() {
		return { value: "__select_all_brands__", label: "Select all" };
	}

	isSelectAllOption(option) {
		return option && option.value === this.selectAllOption().value;
	}

	isAllOptionsSelected(options, selectedOptions) {
		const optionList = Array.isArray(options) ? options : [];
		const optionValues = {};
		optionList.forEach((option) => {
			if (option && option.value != null) optionValues[String(option.value)] = true;
		});
		const selectedCount = (Array.isArray(selectedOptions) ? selectedOptions : []).filter(
			(option) => option && optionValues[String(option.value)],
		).length;
		return optionList.length > 0 && selectedCount === optionList.length;
	}

	optionsWithSelectAll(options) {
		const optionList = Array.isArray(options) ? options : [];
		return optionList.length ? [this.selectAllOption(), ...optionList] : optionList;
	}

	selectedOptionsWithSelectAll(options, selectedOptions) {
		const selected = Array.isArray(selectedOptions) ? selectedOptions : [];
		return this.isAllOptionsSelected(options, selected) ? [this.selectAllOption(), ...selected] : selected;
	}

	resolveSelectAllChange(nextSelectedOptions, options, previousSelectedOptions) {
		const optionList = Array.isArray(options) ? options : [];
		const optionValues = {};
		optionList.forEach((option) => {
			if (option && option.value != null) optionValues[String(option.value)] = true;
		});
		const selected = Array.isArray(nextSelectedOptions) ? nextSelectedOptions : [];
		const hasSelectAll = selected.some((option) => this.isSelectAllOption(option));
		const nextOptions = selected.filter((option) => option && optionValues[String(option.value)]);
		const wasAllSelected = this.isAllOptionsSelected(optionList, previousSelectedOptions);

		if (!optionList.length) return [];
		if (hasSelectAll && !wasAllSelected) return optionList;
		if (!hasSelectAll && wasAllSelected && nextOptions.length === optionList.length) return [];
		return nextOptions;
	}

	renderOptionTiles(selected, disabled, emptyText, onRemove) {
		const editingDisabled = !!disabled;
		return selected.length ? (
			<div
				style={{
					display: "grid",
					gap: "6px",
					gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))",
				}}>
				{selected.map((option) => (
					<div
						key={option.value}
						role="button"
						tabIndex={editingDisabled ? -1 : 0}
						title={editingDisabled ? option.label : "Remove " + option.label}
						aria-disabled={editingDisabled}
						onClick={() => {
							if (editingDisabled) return;
							onRemove(selected.filter((o) => o.value !== option.value));
						}}
						onKeyDown={(e) => {
							if (editingDisabled) return;
							if (e.key !== "Enter" && e.key !== " ") return;
							e.preventDefault();
							onRemove(selected.filter((o) => o.value !== option.value));
						}}
						style={{
							border: "1px solid #0972d3",
							borderRadius: "8px",
							boxSizing: "border-box",
							cursor: editingDisabled ? "default" : "pointer",
							display: "flex",
							gap: "8px",
							justifyContent: "space-between",
							minHeight: "36px",
							overflow: "hidden",
							padding: "7px 10px",
							width: "100%",
						}}>
						<span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{option.label}</span>
						{editingDisabled ? null : <span aria-hidden="true">x</span>}
					</div>
				))}
			</div>
		) : (
			<Box variant="small" color="text-body-secondary">
				{emptyText}
			</Box>
		);
	}

	renderGroupTiles(selected, disabled, onRemove) {
		const groupEditingDisabled = !!disabled;
		return selected.length ? (
			<div
				style={{
					display: "grid",
					gap: "6px",
					gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))",
				}}>
				{selected.map((option) => (
					<div
						key={option.value}
						role="button"
						tabIndex={groupEditingDisabled ? -1 : 0}
						title={groupEditingDisabled ? option.label : "Remove " + option.label}
						aria-disabled={groupEditingDisabled}
						onClick={() => {
							if (groupEditingDisabled) return;
							const ids = selected.filter((o) => o.value !== option.value).map((o) => Number(o.value));
							onRemove(ids);
						}}
						onKeyDown={(e) => {
							if (groupEditingDisabled) return;
							if (e.key !== "Enter" && e.key !== " ") return;
							e.preventDefault();
							const ids = selected.filter((o) => o.value !== option.value).map((o) => Number(o.value));
							onRemove(ids);
						}}
						style={{
							border: "1px solid #0972d3",
							borderRadius: "8px",
							boxSizing: "border-box",
							cursor: groupEditingDisabled ? "default" : "pointer",
							display: "flex",
							gap: "8px",
							justifyContent: "space-between",
							minHeight: "36px",
							overflow: "hidden",
							padding: "7px 10px",
							width: "100%",
						}}>
						<span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{option.label}</span>
						{groupEditingDisabled ? null : <span aria-hidden="true">x</span>}
					</div>
				))}
			</div>
		) : (
			<Box variant="small" color="text-body-secondary">
				No groups selected
			</Box>
		);
	}

	renderForm() {
		const state = this.props.staffAddModal;
		const { form, groupsLoaded, groupsError, visibleProjectsLoaded, visibleProjectsError, error } = state;
		const callerLevel = Number(this.props.user.level) || 0;
		const maxAssignableLevel = Math.max(0, callerLevel - 1);
		const levelOptions = [];
		for (let i = 1; i <= maxAssignableLevel; i += 1) {
			levelOptions.push({ value: String(i), label: String(i) });
		}
		const selectedLevel = levelOptions.find((option) => option.value === String(form.level)) || null;

		if ((!groupsLoaded && !groupsError) || (!visibleProjectsLoaded && !visibleProjectsError)) {
			return (
				<Box textAlign="center" margin={{ top: "xxl", bottom: "xxl" }}>
					<Spinner size="large" />
				</Box>
			);
		}

		const brandOptions = this.brandOptions();
		const selectedBrandIdSet = {};
		(form.brandIds || []).forEach((brandId) => {
			selectedBrandIdSet[String(brandId)] = true;
		});
		const selectedBrandOptions = brandOptions.filter(
			(o) => selectedBrandIdSet[o.value],
		);
		const brandOptionsWithSelectAll = this.optionsWithSelectAll(brandOptions);
		const selectedBrandOptionsWithSelectAll = this.selectedOptionsWithSelectAll(brandOptions, selectedBrandOptions);

		const groupOptions = this.groupOptions();
		const mapGroupIdsToOptions = (ids) =>
			(ids || []).map((id) => {
				const hit = groupOptions.find((o) => o.value === String(id));
				return hit || { value: String(id), label: "#" + id };
			});

		const selectedDefaultGroups = mapGroupIdsToOptions(form.defaultGroupIds);

		return (
			<SpaceBetween size="l">
				<Container
					header={
						<Header variant="h3" description="Set the base credentials and maximum access level.">
							Account
						</Header>
					}>
					<SpaceBetween size="s">
						<ColumnLayout columns={2}>
							<SpaceBetween size="xs">
								<FormField label="Login (4..64)">
									<Input
										value={form.login}
										disableBrowserAutocorrect={true}
										onChange={(e) => this.updateForm({ login: e.detail.value })}
									/>
								</FormField>
							</SpaceBetween>
							<FormField label="Password (8..128)">
								<SpaceBetween size="xs">
									<Input
										type={this.state.showPassword ? "text" : "password"}
										value={form.password}
										disableBrowserAutocorrect={true}
										onChange={(e) => this.updateForm({ password: e.detail.value })}
									/>
									<Box textAlign="right">
										<Button wrapText={false} onClick={() => this.setState({ showPassword: !this.state.showPassword })}>
											{this.state.showPassword ? "Hide" : "Show"}
										</Button>
									</Box>
								</SpaceBetween>
							</FormField>
						</ColumnLayout>
					</SpaceBetween>
				</Container>

				<Container
					header={
						<Header variant="h3" description="Choose target projects and one group set for all of them.">
							Project access
						</Header>
					}>
					<SpaceBetween size="s">
						<FormField label={`Level (1..${maxAssignableLevel})`}>
							<Select
								options={levelOptions}
								selectedOption={selectedLevel}
								placeholder="Choose level"
								onChange={(e) => this.updateForm({ level: e.detail.selectedOption.value })}
							/>
						</FormField>
						<FormField
							label="Projects"
							description="Pick every brand where this user should be created. Only brands visible to you are listed.">
							<Multiselect
								placeholder="Choose projects"
								options={brandOptionsWithSelectAll}
								selectedOptions={selectedBrandOptionsWithSelectAll}
								hideTokens
								disabled={!!visibleProjectsError}
								onChange={(e) => {
									const nextOptions = this.resolveSelectAllChange(e.detail.selectedOptions || [], brandOptions, selectedBrandOptions);
									const nextIds = nextOptions.map((o) => Number(o.value));
									this.setBrandIds(nextIds);
								}}
								deselectAriaLabel={(o) => `Remove ${o.label}`}
								empty={visibleProjectsError ? "Visible projects failed to load" : "No visible projects available"}
							/>
						</FormField>
						{this.renderOptionTiles(
							selectedBrandOptions,
							!!visibleProjectsError,
							"No projects selected",
							(nextOptions) => this.setBrandIds(nextOptions.map((option) => Number(option.value))),
						)}
						{visibleProjectsLoaded && !visibleProjectsError && brandOptions.length === 0 ? (
							<Alert type="warning">No visible projects are available for your account.</Alert>
						) : null}

						{groupsError ? (
							<Alert
								type="error"
								header="Unable to load groups"
								action={<Button onClick={() => this.loadGroups()}>Retry</Button>}>
								{groupsError}
							</Alert>
						) : (
							<>
								<FormField
									label="Groups"
									description="These groups will be applied to all selected projects. You can adjust per-project groups later in Access.">
									<Multiselect
										options={groupOptions}
										selectedOptions={selectedDefaultGroups}
										hideTokens
										placeholder="Choose groups"
										onChange={(e) => {
											const ids = (e.detail.selectedOptions || []).map((o) => Number(o.value));
											this.updateForm({ defaultGroupIds: ids });
										}}
										empty="No groups available"
									/>
								</FormField>
								{this.renderGroupTiles(selectedDefaultGroups, false, (ids) => this.updateForm({ defaultGroupIds: ids }))}
							</>
						)}
					</SpaceBetween>
				</Container>

				<Container
					header={
						<Header variant="h3" description="A shared 2FA secret will be generated for the new user.">
							Security
						</Header>
					}>
					<Alert type="info">
						2FA is enabled by default for new staff users. A single shared 2FA secret will be generated and used on every selected project.
					</Alert>
				</Container>

				{visibleProjectsError ? <Alert type="error">{visibleProjectsError}</Alert> : null}
				{error && error.text && error.text !== visibleProjectsError && error.text !== groupsError ? <Alert type="error">{error.text}</Alert> : null}
			</SpaceBetween>
		);
	}

	renderPending() {
		const brandCount = (this.props.staffAddModal.form.brandIds || []).length;
		return (
			<Box textAlign="center" margin={{ top: "xxl", bottom: "xxl" }}>
				<Spinner size="large" />
				<Box variant="p" margin={{ top: "m" }}>
					Creating user on {brandCount} project{brandCount === 1 ? "" : "s"}...
				</Box>
			</Box>
		);
	}

	renderSuccess() {
		const state = this.props.staffAddModal;
		const { form, result } = state;
		const secret = result.secret;
		const brands = result.brands || [];

		return (
			<SpaceBetween size="l">
				<Container
					header={
						<Header variant="h3" description="Create operation completed.">
							Result
						</Header>
					}>
					<Alert type="success">
						User `{form.login}` created on {brands.length} project{brands.length === 1 ? "" : "s"}.
					</Alert>
				</Container>

				<Container
					header={
						<Header variant="h3" description="Save the generated secret before closing this modal.">
							2FA secret
						</Header>
					}>
					{typeof secret === "string" && secret.length ? (
						<SpaceBetween size="s">
							<Box variant="small" color="text-body-secondary">
								Save this secret now — it won't be shown again.
							</Box>
							<pre style={{ margin: 0, wordBreak: "break-all", whiteSpace: "pre-wrap" }}>{secret}</pre>
							<Box>
								<Button
									iconName="copy"
									onClick={() => this.onCopy(secret, "2FA secret")}>
									Copy secret
								</Button>
							</Box>
						</SpaceBetween>
					) : (
						<Box variant="small" color="text-body-secondary">
							This user was created without 2FA. They will sign in with password only until a secret is set via Reset 2FA.
						</Box>
					)}
				</Container>

				<Container
					header={
						<Header variant="h3" description="Brand rows created for this user.">
							Created accounts
						</Header>
					}>
					<Table
						wrapLines={true}
						items={brands}
						empty="No brands"
						columnDefinitions={[
							{
								id: "brand",
								header: "Project",
								cell: (item) => (
									<>
										<Box variant="span">{item.brandName}</Box>
										<Box variant="small" color="text-body-secondary">
											#{item.brandId}
										</Box>
									</>
								),
							},
							{
								id: "userId",
								header: "panel_users.id",
								cell: (item) => item.userId,
							},
						]}
					/>
				</Container>
			</SpaceBetween>
		);
	}

	renderBody() {
		const stage = this.props.staffAddModal.stage;
		if (stage === "pending") return this.renderPending();
		if (stage === "success") return this.renderSuccess();
		return this.renderForm();
	}

	renderFooter() {
		const state = this.props.staffAddModal;
		const stage = state.stage;

		if (stage === "pending") {
			return (
				<Box float="right">
					<Button variant="normal" disabled={true}>Close</Button>
				</Box>
			);
		}
		if (stage === "success") {
			return (
				<Box float="right">
					<Button variant="primary" onClick={() => this.onDone()}>Done</Button>
				</Box>
			);
		}
		const visibleProjectIds = {};
		(state.visibleProjects || []).forEach((project) => {
			if (project && project.brand != null) visibleProjectIds[String(project.brand)] = true;
		});
		const visibleSelectedCount = (state.form.brandIds || []).filter((brandId) => visibleProjectIds[String(brandId)]).length;
		const canCreate = state.groupsLoaded && !state.groupsError && state.visibleProjectsLoaded && !state.visibleProjectsError && visibleSelectedCount > 0;
		return (
			<Box float="right">
				<SpaceBetween direction="horizontal" size="xs">
					<Button variant="normal" onClick={() => this.onClose()}>Close</Button>
					<Button variant="primary" disabled={!canCreate} onClick={() => this.onCreate()}>Create</Button>
				</SpaceBetween>
			</Box>
		);
	}

	render() {
		const stage = this.props.staffAddModal.stage;
		return (
			<Modal
				size="large"
				visible={this.props.staffAddModal.show}
				onDismiss={() => {
					if (stage === "pending") return;
					this.onClose();
				}}
				header={<Header>Add user</Header>}
				footer={this.renderFooter()}>
				{this.renderBody()}
			</Modal>
		);
	}
}

function withRecoil(Component) {
	return function WrappedComponent(props) {
		const staff = useRecoilValue(staffStore);
		const setStaff = useSetRecoilState(staffStore);
		const staffAddModal = useRecoilValue(staffAddModalStore);
		const setStaffAddModal = useSetRecoilState(staffAddModalStore);
		const config = useRecoilValue(configStore);
		const user = useRecoilValue(userStore);

		return (
			<Component
				{...props}
				staff={staff}
				setStaff={setStaff}
				user={user}
				staffAddModal={staffAddModal}
				setStaffAddModal={setStaffAddModal}
				config={config}
			/>
		);
	};
}

export default withRecoil(StaffAddUserModal);
